import React, { Component } from 'react';

import axios from 'axios';
import moment from 'moment';

import store from 'store';

import { Download, Inbox, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
	AnswerView,
	answerToCsv,
	hasAnswer,
} from '@/components/forms/responses/answerRenderers';

const escapeCsvCell = (value) => {
	if (value == null) return '';
	const str = String(value);
	return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

// Build and download a CSV from an array of flat objects (keys become columns).
const downloadCsv = (rows, filename) => {
	const safeRows = rows.length
		? rows
		: [{ Question: '', Response: '', Date: '' }];
	const headers = Object.keys(safeRows[0]);

	const csv = [headers, ...safeRows.map((row) => headers.map((h) => row[h]))]
		.map((line) => line.map(escapeCsvCell).join(','))
		.join('\n');

	const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

const sortResponsesNewestFirst = (responses = []) =>
	[...responses].sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1));

const sortQuestions = (questions = []) =>
	[...questions].sort((a, b) => {
		if (typeof a.index === 'number' && typeof b.index === 'number') {
			return a.index > b.index ? 1 : -1;
		}
		return a.createdAt > b.createdAt ? 1 : -1;
	});

class CustomerQuestionnaireDialog extends Component {
	state = {
		loading: true,
		questions: [],
		customerName: '',
		onlyShowLatestResponseOnly: true,
	};

	async componentDidMount() {
		const onlyShowLatestResponseStoreValue = store.get(
			'questionnaire-dialog-only-show-latest-response'
		);

		if (typeof onlyShowLatestResponseStoreValue === 'boolean') {
			this.setState({
				onlyShowLatestResponseOnly: onlyShowLatestResponseStoreValue,
			});
		}

		const res = await axios.get(
			`/api/feedback/customer-response/${this.props._questionnaireRequest}`
		);

		const { data } = res;

		if (data.customerName) {
			this.setState({ customerName: data.customerName });
		}

		// responses is an object: {question: String, answers: String[]}
		if (data.questions) {
			return this.setState({ questions: data.questions, loading: false });
		}

		return this.setState({ loading: false, questions: [] });
	}

	closeDialog = () => {
		this.props.closeDialog();
	};

	toggleLatestOnly = () => {
		this.setState((prev) => {
			const next = !prev.onlyShowLatestResponseOnly;
			store.set('questionnaire-dialog-only-show-latest-response', next);
			return { onlyShowLatestResponseOnly: next };
		});
	};

	// Header meta: when the form was submitted and how many of its questions
	// have an answer (based on each question's most recent response).
	getSummary = () => {
		const { questions } = this.state;

		const allResponses = questions.reduce(
			(acc, q) => acc.concat(q.responses || []),
			[]
		);
		const latest = sortResponsesNewestFirst(allResponses)[0];

		const answered = questions.filter((q) => {
			const latestForQuestion = sortResponsesNewestFirst(q.responses)[0];
			return latestForQuestion && hasAnswer(q.type, latestForQuestion);
		}).length;

		return {
			submittedDate: latest
				? moment(latest.createdAt).format('ll')
				: '',
			answered,
			total: questions.length,
		};
	};

	getResponses = (question, onlyShowLatestResponseOnly) => {
		const responses = sortResponsesNewestFirst(question.responses);

		if (!responses.length) {
			return (
				<p className="text-sm italic text-muted-foreground">No answer</p>
			);
		}

		const visible = onlyShowLatestResponseOnly
			? responses.slice(0, 1)
			: responses;

		return (
			<div className="space-y-2">
				{visible.map((response, i) => (
					<div key={response._id || response.createdAt}>
						{i > 0 && <Separator className="my-2" />}
						{hasAnswer(question.type, response) ? (
							<AnswerView
								type={question.type}
								response={response}
							/>
						) : (
							<p className="text-sm italic text-muted-foreground">
								No answer
							</p>
						)}
						{!onlyShowLatestResponseOnly && (
							<p className="mt-1 text-xs text-muted-foreground">
								{moment(response.createdAt).format(
									'll'
								)}
							</p>
						)}
					</div>
				))}
			</div>
		);
	};

	renderQuestions() {
		const { questions, onlyShowLatestResponseOnly } = this.state;

		return (
			<div className="space-y-5">
				{sortQuestions(questions).map((question, i) => (
					<div
						key={question._id || question.text}
						className="space-y-1.5"
					>
						<div className="flex items-baseline gap-2">
							<span className="text-xs font-semibold text-muted-foreground">
								{i + 1}.
							</span>
							<p className="text-sm font-medium text-foreground">
								{question.text || 'Untitled question'}
							</p>
						</div>
						<div className="pl-5">
							{this.getResponses(
								question,
								onlyShowLatestResponseOnly
							)}
						</div>
					</div>
				))}
			</div>
		);
	}

	exportQuestionnaireResponses = () => {
		const { questions, customerName, onlyShowLatestResponseOnly } =
			this.state;

		const filename = `Questionnaire Responses - ${customerName || ''}.csv`;

		const rows = sortQuestions(questions).map((question) => {
			const responses = sortResponsesNewestFirst(question.responses);
			const visible = onlyShowLatestResponseOnly
				? responses.slice(0, 1)
				: responses;

			const answer = visible
				.map((response) => answerToCsv(question.type, response))
				.filter(Boolean)
				.join(' | ');

			const date = responses[0]
				? moment(responses[0].createdAt).format('ll')
				: '';

			return {
				Question: question.text || '',
				Response: answer,
				Date: date,
			};
		});

		downloadCsv(rows, filename);
	};

	render() {
		const { loading, questions, customerName, onlyShowLatestResponseOnly } =
			this.state;

		const hasQuestions = Boolean(questions?.length);
		const { submittedDate, answered, total } = this.getSummary();

		const description =
			[customerName, submittedDate && `Submitted ${submittedDate}`]
				.filter(Boolean)
				.join(' · ') || 'Customer questionnaire';

		return (
			<Dialog
				open={this.props.open || false}
				onOpenChange={(x) => {
					if (!x) this.props.closeDialog();
				}}
			>
				<DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
					<DialogHeader className="shrink-0 space-y-1 border-b border-border px-6 py-4 text-left">
						<DialogTitle className="text-lg">
							Form responses
						</DialogTitle>
						<DialogDescription>{description}</DialogDescription>
					</DialogHeader>

					{!loading && hasQuestions && (
						<div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-6 py-3">
							<Label
								htmlFor="latest-only"
								className="flex cursor-pointer items-center gap-2 text-sm font-normal text-muted-foreground"
							>
								<Switch
									id="latest-only"
									checked={onlyShowLatestResponseOnly}
									onCheckedChange={this.toggleLatestOnly}
								/>
								Latest only
							</Label>
							<div className="flex items-center gap-3">
								<span className="text-xs tabular-nums text-muted-foreground">
									{answered}/{total} answered
								</span>
								<Button
									variant="outline"
									size="sm"
									onClick={this.exportQuestionnaireResponses}
								>
									<Download className="mr-1.5 h-4 w-4" />
									Export CSV
								</Button>
							</div>
						</div>
					)}

					<div className="min-h-0 flex-1 overflow-y-auto">
						<div className="px-6 py-5">
							{loading ? (
								<div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
									<Loader2 className="h-6 w-6 animate-spin" />
									<p className="text-sm">
										Loading responses…
									</p>
								</div>
							) : hasQuestions ? (
								this.renderQuestions()
							) : (
								<div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-muted-foreground">
									<Inbox className="h-8 w-8 opacity-50" />
									<p className="text-sm font-medium">
										No responses found
									</p>
									<p className="text-xs">
										This questionnaire has no recorded
										answers.
									</p>
								</div>
							)}
						</div>
					</div>
				</DialogContent>
			</Dialog>
		);
	}
}

export default CustomerQuestionnaireDialog;
